// The analysis cache (P3.8) — FR-12, Architecture §5's "IndexedDB records … waveform blobs in OPFS".
//
// **A shell, and deliberately a thin one.** IndexedDB and OPFS exist only in a browser, so nothing
// in this file can run under Node — the same position `analysis-host.ts` and `render-host.ts` are in.
// Everything that could be *wrong* therefore lives in `platform/types/library.ts`, which is pure and
// tested: the record shape, the schema version, and the predicates that decide whether a stored
// record may be used. What is left here is opening two stores and moving bytes.
//
// ## Every failure is a miss
//
// P3.8's verify asks that "the browser's storage quota being full degrades to *analyse every time*
// rather than to a broken deck", and the way to get that is to make it structural rather than
// remembered: **every call below returns a miss or a false on failure and never throws.** A cache is
// an optimisation; the only correct behaviour when it cannot work is to be absent. Nothing above
// this file has an error path for the cache, because there is nothing it could usefully do.
//
// That covers more than quota. A user browsing in a private window, a browser with storage
// permission denied, an OPFS blob evicted while its IndexedDB row survives, a half-written file
// after a crash — all of them arrive here as an exception or a wrong length, and all of them should
// come out as "analyse it again".
//
// ## Why the two stores are split the way they are
//
// The record is a few hundred bytes of numbers and the waveform is megabytes. In one store, every
// lookup of a BPM would read the whole waveform to get at it. Split, a miss costs one small row read
// and a hit costs one small row plus exactly one blob.

import {
  ANALYSIS_SCHEMA_VERSION,
  CACHE_STORE_NAMES,
  blobUsability,
  isCachedAnalysis,
  isCachedGridOverride,
  isCachedHotCues,
  usabilityOf,
  type CachedGridOverride,
  type CachedHotCues,
  type CachedAnalysis,
  type CacheMiss,
  type ContentHash,
  type DecodedTrackIdentity,
} from '../platform/types/library';

const DATABASE_NAME = 'synapse-analysis';
/**
 * The store names, taken from the shared list rather than written out here.
 *
 * ⚠️ `CACHE_STORE_NAMES` is pinned by a test against exactly these three, so a fourth store added
 * without bumping `ANALYSIS_SCHEMA_VERSION` goes red in Node. That is the guard for the bug P3.10
 * shipped — a store added while the version stood still is never created for an existing database,
 * and every write to it fails into a `catch` that looks like an ordinary cache miss.
 */
const [RECORD_STORE, CUE_STORE_NAME, OVERRIDE_STORE_NAME] = CACHE_STORE_NAMES;
/**
 * Hot cues, in their own store (P3.10).
 *
 * ⚠️ **Not wiped by a schema upgrade, and that is the entire reason it is separate.**
 * `ANALYSIS_SCHEMA_VERSION` throws away every analysis record when a detector improves — right for a
 * BPM the app computed, catastrophic for a cue point a human placed. The upgrade below deletes
 * `records` and leaves this alone.
 */
const CUE_STORE = CUE_STORE_NAME;
/**
 * Manual grid corrections, in their own store (P3.12) — FR-13.
 *
 * ⚠️ **Survives a schema upgrade for a sharper reason than the cues do.** A cue is user data the
 * upgrade must not lose. A grid override is user data that exists *because the detector was wrong* —
 * so wiping it when `ANALYSIS_SCHEMA_VERSION` bumps would throw away the correction at exactly the
 * moment the thing it corrects has changed, which is when the user is least likely to notice.
 *
 * A better detector does not overrule a human who tapped along to the track. If it ever should, that
 * is a deliberate decision with a UI attached, not a side effect of a version number.
 */
const OVERRIDE_STORE = OVERRIDE_STORE_NAME;
const BLOB_DIRECTORY = 'analysis-mipmaps';

/**
 * The IndexedDB version **is** the analysis schema version, and that is the whole invalidation
 * mechanism (P3.8's "invalidatable by schema version").
 *
 * Bumping `ANALYSIS_SCHEMA_VERSION` therefore fires `onupgradeneeded`, where the store is deleted and
 * recreated — every stale record gone in one transaction the browser makes atomic for us, rather
 * than a migration to write and get wrong. The blobs are swept separately (see `#sweepBlobs`),
 * because OPFS is not part of that transaction and cannot be.
 *
 * The alternative was checking the version per record on read and leaving the rest. That is also
 * needed, and `usabilityOf` does it — but on its own it would leave a whole generation of dead
 * records occupying the user's quota forever, which is how a cache becomes a leak.
 */
const DATABASE_VERSION = ANALYSIS_SCHEMA_VERSION;

export interface CacheHit {
  readonly record: CachedAnalysis;
  /**
   * The mipmap buffer, in shared memory and ready to hand to the waveform worker.
   *
   * A `SharedArrayBuffer` rather than the `ArrayBuffer` OPFS returns, because that is what every
   * consumer of `TrackAnalysis.mipmaps` expects and the copy has to happen somewhere. One memcpy of
   * a few megabytes, against the seconds of DSP it replaces.
   */
  readonly mipmaps: SharedArrayBuffer;
}

export type CacheLookup =
  | { readonly hit: CacheHit; readonly miss?: undefined }
  | { readonly hit?: undefined; readonly miss: CacheMiss };

/** Promisify one IndexedDB request. Rejections are handled by every caller, never propagated. */
function request<T>(source: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    source.onsuccess = (): void => {
      resolve(source.result);
    };
    source.onerror = (): void => {
      reject(source.error ?? new Error('IndexedDB request failed.'));
    };
  });
}

export class AnalysisCache {
  readonly #db: IDBDatabase;
  #blobs: FileSystemDirectoryHandle | null = null;

  private constructor(db: IDBDatabase) {
    this.#db = db;
  }

  /**
   * Opens both stores, or returns **null** when the browser will not have it.
   *
   * Null rather than a throw, and rather than a no-op object: the caller's branch is
   * `cache === null ? analyse() : …`, and a null makes "there is no cache" a state the type system
   * mentions. A silent no-op cache would be indistinguishable from one that never hits, which is
   * exactly the kind of thing this repo keeps finding at P0.1, P0.10 and P2.12.
   */
  static async open(): Promise<AnalysisCache | null> {
    try {
      // ⚠️ **Inside the try, and that is not tidiness.** `indexedDB` is a getter, and in a
      // sandboxed frame or with storage blocked it *throws* on access rather than being undefined —
      // Chrome raises `SecurityError` when third-party storage is denied. With this check outside
      // the try, that exception escaped `open()`, rejected the promise `App` memoises, and every
      // subsequent load failed with "Couldn't read this file". Caught by the test for the case this
      // file's header promises to handle, which is the only reason it is not shipping.
      if (typeof indexedDB === 'undefined') {
        return null;
      }

      // Resolved out of the promise rather than captured in a closure variable: the upgrade
      // handler runs inside it, and a `let` flag mutated in there is invisible to the type checker,
      // which then reads the branch below as dead.
      const { db, upgraded } = await new Promise<{
        db: IDBDatabase;
        upgraded: boolean;
      }>((resolve, reject) => {
        let didUpgrade = false;
        const opening = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
        opening.onupgradeneeded = (): void => {
          didUpgrade = true;
          const database = opening.result;
          // Delete rather than migrate. A record's *meaning* changed, not its shape — see
          // ANALYSIS_SCHEMA_VERSION — so there is nothing in the old rows worth carrying forward.
          if (database.objectStoreNames.contains(RECORD_STORE)) {
            database.deleteObjectStore(RECORD_STORE);
          }
          database.createObjectStore(RECORD_STORE, { keyPath: 'hash' });
          // Created once and never dropped — see CUE_STORE. A user's cue points survive every
          // detector improvement this project will ever ship.
          if (!database.objectStoreNames.contains(CUE_STORE)) {
            database.createObjectStore(CUE_STORE, { keyPath: 'hash' });
          }
          // Likewise, and see OVERRIDE_STORE for why this one matters even more than the cues.
          if (!database.objectStoreNames.contains(OVERRIDE_STORE)) {
            database.createObjectStore(OVERRIDE_STORE, { keyPath: 'hash' });
          }
        };
        opening.onsuccess = (): void => {
          resolve({ db: opening.result, upgraded: didUpgrade });
        };
        opening.onerror = (): void => {
          reject(opening.error ?? new Error('Could not open the analysis cache.'));
        };
        // Another tab holds an older version open. Rather than hang forever waiting for it, treat
        // this as "no cache today" — the deck still works, it just analyses.
        opening.onblocked = (): void => {
          reject(
            new Error('The analysis cache is open in another tab at an older version.'),
          );
        };
      });

      const cache = new AnalysisCache(db);
      if (upgraded) {
        // Outside the IndexedDB transaction, because OPFS cannot join one. A failure here leaks
        // blobs rather than corrupting anything: the records that referenced them are already gone,
        // so they are unreachable, and the next upgrade sweeps again.
        await cache.#sweepBlobs();
      }
      return cache;
    } catch (cause: unknown) {
      console.warn(
        '[synapse] analysis cache unavailable; tracks will be re-analysed',
        cause,
      );
      return null;
    }
  }

  /** The OPFS directory, opened once. Null whenever OPFS is unavailable or refuses. */
  async #directory(): Promise<FileSystemDirectoryHandle | null> {
    if (this.#blobs !== null) {
      return this.#blobs;
    }
    try {
      const root = await navigator.storage.getDirectory();
      this.#blobs = await root.getDirectoryHandle(BLOB_DIRECTORY, { create: true });
      return this.#blobs;
    } catch {
      return null;
    }
  }

  /** Drops every stored waveform. Called after a schema upgrade, when they are all unreachable. */
  async #sweepBlobs(): Promise<void> {
    try {
      const root = await navigator.storage.getDirectory();
      await root.removeEntry(BLOB_DIRECTORY, { recursive: true });
    } catch {
      // Nothing there, or OPFS unavailable. Both fine — the point was that nothing survives.
    }
    this.#blobs = null;
  }

  /**
   * Looks a track up. **Never throws**; a failure of any kind is a miss.
   *
   * The record is checked before the blob is opened, so the common miss — a track never analysed —
   * costs one small row read and no file system work at all.
   */
  async read(track: DecodedTrackIdentity): Promise<CacheLookup> {
    let record: CachedAnalysis | null = null;
    try {
      const store = this.#db
        .transaction(RECORD_STORE, 'readonly')
        .objectStore(RECORD_STORE);
      // `store.get` is typed `any` by lib.dom, which is honest — IndexedDB really can hand back
      // whatever was put in, including by devtools. `isCachedAnalysis` is where that becomes a
      // record or a miss.
      const stored: unknown = await request<unknown>(store.get(track.hash));
      record = isCachedAnalysis(stored) ? stored : null;
    } catch {
      return { miss: 'no-record' };
    }

    const unusable = usabilityOf(record, track);
    if (unusable !== null || record === null) {
      return { miss: unusable ?? 'no-record' };
    }

    let bytes: ArrayBuffer | null = null;
    try {
      const directory = await this.#directory();
      const handle = await directory?.getFileHandle(blobName(record.hash));
      const file = await handle?.getFile();
      bytes = (await file?.arrayBuffer()) ?? null;
    } catch {
      bytes = null;
    }

    const blobProblem = blobUsability(record, bytes?.byteLength ?? null);
    if (blobProblem !== null || bytes === null) {
      return { miss: blobProblem ?? 'blob-missing' };
    }

    // The layout is recomputed by the caller and compared; here we only guarantee the buffer is the
    // size the record claims, which `blobUsability` has just established.
    const mipmaps = new SharedArrayBuffer(bytes.byteLength);
    new Uint8Array(mipmaps).set(new Uint8Array(bytes));
    return { hit: { record, mipmaps } };
  }

  /**
   * Stores a record and its waveform. Returns whether it stuck.
   *
   * **The blob is written first, and that ordering is the point.** If the blob write fails on quota
   * and the row were already in, every future lookup would find a record whose waveform is missing —
   * a miss, but an expensive one that reads a row and opens a file before finding out. Writing the
   * blob first means a failure leaves nothing behind, and the next load is an ordinary first load.
   *
   * A blob without a row is the harmless direction: unreachable bytes that the next schema bump
   * sweeps.
   */
  async write(record: CachedAnalysis, mipmaps: ArrayBufferLike): Promise<boolean> {
    try {
      const directory = await this.#directory();
      if (directory === null) {
        return false;
      }
      const handle = await directory.getFileHandle(blobName(record.hash), {
        create: true,
      });
      const writable = await handle.createWritable();
      // A plain `ArrayBuffer` copy: `write` will not accept a view onto shared memory in every
      // browser, and the copy is one memcpy against a DSP pipeline measured in seconds.
      const copy = new Uint8Array(mipmaps.byteLength);
      copy.set(new Uint8Array(mipmaps as ArrayBuffer));
      await writable.write(copy);
      await writable.close();
    } catch (cause: unknown) {
      console.warn(
        '[synapse] could not store the waveform; this track will be re-analysed',
        cause,
      );
      return false;
    }

    try {
      const transaction = this.#db.transaction(RECORD_STORE, 'readwrite');
      await request(transaction.objectStore(RECORD_STORE).put(record));
      return true;
    } catch (cause: unknown) {
      console.warn('[synapse] could not store the analysis record', cause);
      return false;
    }
  }

  /** A track's stored cues, or null. Never throws; a failure is "no cues", like every miss here. */
  async readHotCues(hash: ContentHash): Promise<CachedHotCues | null> {
    try {
      const store = this.#db.transaction(CUE_STORE, 'readonly').objectStore(CUE_STORE);
      const stored: unknown = await request<unknown>(store.get(hash));
      return isCachedHotCues(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  /**
   * Stores a track's cues. Returns whether it stuck.
   *
   * No blob, so unlike `write` this is one small row and cannot half-succeed. A quota failure still
   * degrades the same way: the cues stay in this session and are gone from the next one, which is
   * the same outcome as never having had a cache.
   */
  async writeHotCues(record: CachedHotCues): Promise<boolean> {
    try {
      const transaction = this.#db.transaction(CUE_STORE, 'readwrite');
      await request(transaction.objectStore(CUE_STORE).put(record));
      return true;
    } catch (cause: unknown) {
      console.warn('[synapse] could not store hot cues', cause);
      return false;
    }
  }

  /** A track's manual grid correction, or null (P3.12). A failure is "no correction", as everywhere
   * else here — the detected grid then stands, which is the pre-FR-13 behaviour. */
  async readGridOverride(hash: ContentHash): Promise<CachedGridOverride | null> {
    try {
      const store = this.#db
        .transaction(OVERRIDE_STORE, 'readonly')
        .objectStore(OVERRIDE_STORE);
      const stored: unknown = await request<unknown>(store.get(hash));
      return isCachedGridOverride(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  /** Stores a correction. One small row; same quota behaviour as the cues. */
  async writeGridOverride(record: CachedGridOverride): Promise<boolean> {
    try {
      const transaction = this.#db.transaction(OVERRIDE_STORE, 'readwrite');
      await request(transaction.objectStore(OVERRIDE_STORE).put(record));
      return true;
    } catch (cause: unknown) {
      console.warn('[synapse] could not store the grid correction', cause);
      return false;
    }
  }

  /**
   * Removes a correction, restoring the detected grid.
   *
   * **A delete rather than a stored "none".** The absence of a row already means "no correction",
   * and a tombstone would be a second way to say the same thing — which is how the next reader ends
   * up handling one and not the other.
   */
  async clearGridOverride(hash: ContentHash): Promise<boolean> {
    try {
      const transaction = this.#db.transaction(OVERRIDE_STORE, 'readwrite');
      await request(transaction.objectStore(OVERRIDE_STORE).delete(hash));
      return true;
    } catch (cause: unknown) {
      console.warn('[synapse] could not clear the grid correction', cause);
      return false;
    }
  }

  close(): void {
    this.#db.close();
  }
}

function blobName(hash: ContentHash): string {
  return `${hash}.mipmaps`;
}
